import React, { useState, useEffect } from 'react';
import UsersManagementModal from '../components/UsersManagementModal';
import RolesManagementModal from '../components/RolesManagementModal';
import PagesManagementModal from '../components/PagesManagementModal';
import { API_URL } from '../config';
import { Users, ShieldCheck, FileText } from 'lucide-react';

const UsersManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [availablePages, setAvailablePages] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Получаем разрешения по умолчанию для роли
  const getDefaultPermissions = (role) => {
    const defaultPerms = {
      'admin': Object.values(availablePages).flat().map(page => page.id),
      'adminb24': Object.values(availablePages).flat().map(page => page.id),
      'cutterb24': ['search-b24', 'cuttingb24'],
      'drillerb24': ['search-b24', 'drillingb24'],
      'zakalkab24': ['search-b24', 'zakalkab24'],
      'matovkab24': ['search-b24', 'matovkab24'],
      'scladb24': ['search-b24', 'scladb24'],
      'loftb24': ['search-b24', 'loftb24'],
      'upakb24': ['search-b24', 'upakb24'],
      'pickupb24': ['search-b24', 'pickupb24'],
      'sendb24': ['search-b24', 'sendb24'],
      'driverb24': ['search-b24', 'driverb24'],
      'workplaceb24': ['search-b24', 'loftb24'],
      'designer': ['search-b24', 'visualloft'],
      'franchise': ['search-b24', 'visualloft']
    };
    return defaultPerms[role] || ['search-b24'];
  };

  // Загрузка доступных страниц из API (только активные страницы для разрешений)
  const fetchAvailablePages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pages/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const pages = await response.json();
        setAvailablePages(pages);
      }
    } catch (error) {
      console.error('Ошибка загрузки страниц:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const rolesData = await response.json();
        setRoles(rolesData);
      }
    } catch (error) {
      console.error('Ошибка загрузки ролей:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchAvailablePages();
  }, []);

  const handleAddUser = async (userData) => {
    if (!userData.username.trim() || !userData.password.trim()) {
      throw new Error('Имя пользователя и пароль обязательны');
    }

    const userToCreate = {
      ...userData,
      permissions: userData.permissions.length > 0 ? userData.permissions : getDefaultPermissions(userData.role)
    };

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(userToCreate),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при добавлении пользователя');
      }

      await fetchUsers();
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при обновлении пользователя');
      }

      await fetchUsers();
    } catch (error) {
      throw error;
    }
  };

  const handleUpdatePermissions = async (userId, permissions) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ permissions }),
      });

      if (!response.ok) {
        throw new Error('Ошибка при обновлении разрешений');
      }

      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId ? { ...u, permissions } : u
        )
      );
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при удалении пользователя');
      }

      await fetchUsers();
    } catch (error) {
      throw error;
    }
  };

  const handleUpdatePhone = async (userId, phone) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}/phone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при обновлении телефона');
      }

      await fetchUsers();
    } catch (error) {
      throw error;
    }
  };

  const handleAddRole = async (roleData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при создании роли');
      }

      await fetchRoles();
    } catch (error) {
      throw error;
    }
  };

  const handleEditRole = async (roleId, roleData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles/${roleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при обновлении роли');
      }

      await fetchRoles();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Вы уверены, что хотите удалить эту роль?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/roles/${roleId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Ошибка при удалении роли');
        }

        await fetchRoles();
      } catch (error) {
        throw error;
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[320px] text-sm text-gray-500">Загрузка...</div>;
  }

  const tabs = [
    { id: 'users', label: 'Пользователи', count: users.length, icon: Users },
    { id: 'roles', label: 'Роли', count: roles.length, icon: ShieldCheck },
    { id: 'pages', label: 'Страницы', count: Object.values(availablePages).flat().length, icon: FileText },
  ];

  return (
    <div className="min-h-full bg-gray-50 p-2 sm:p-3 md:p-4">
      <div className="w-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <nav className="flex overflow-x-auto border-b border-gray-200 bg-white px-2" aria-label="Разделы настроек">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex min-w-[160px] flex-1 items-center justify-center gap-2 border-b-2 px-4 py-3.5 text-sm font-semibold transition-colors ${isActive ? 'border-blue-600 bg-blue-50/70 text-blue-700' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                <span>{tab.label}</span>
                <span className={`min-w-6 rounded-full px-1.5 py-0.5 text-center text-xs font-medium ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{tab.count}</span>
              </button>
            );
          })}
        </nav>

        {activeTab === 'users' && (
          <UsersManagementModal
            embedded
            isOpen
            onClose={fetchUsers}
            onAddUser={handleAddUser}
            onUpdateUser={handleUpdateUser}
            onUpdatePermissions={handleUpdatePermissions}
            onDeleteUser={handleDeleteUser}
            onUpdatePhone={handleUpdatePhone}
            availablePages={availablePages}
            roles={roles}
            users={users}
          />
        )}

        {activeTab === 'roles' && (
          <RolesManagementModal
            embedded
            isOpen
            onClose={fetchRoles}
            onAddRole={handleAddRole}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
            availablePages={availablePages}
            standardRoles={roles}
          />
        )}

        {activeTab === 'pages' && (
          <PagesManagementModal
            embedded
            isOpen
            onClose={fetchAvailablePages}
          />
        )}
      </div>
    </div>
  );
};

export default UsersManagementPage;
